import React from 'react';
import {connect} from 'react-redux';
import {
    addDays, addMonths, addYears, clamp,
    endOfMonth, endOfYear,
    getYear,
    isSameDay, isSameYear,
    parseISO,
    startOfMonth, startOfToday, startOfYear,
    subMonths, subYears
} from "date-fns";
import {Loading} from "spectre-react/dist/cjs";
import {Calendar, CalendarControls} from './YearCalendar';
import AffectingOverridesLog from "./AffectingOverridesLog";
import OverridesLog from "./OverridesLog";
import StatusLegend from "./StatusLegend";
import MonthCalendar from "./MonthCalendar";
import ManagerActions from "./day_actions/ManagerActions";
import EngineerActions from "./day_actions/EngineerActions";
import EngineerDayContent from "./day_content/EngineerDayContent";
import ManagerDayContent from "./day_content/ManagerDayContent";
import {transformDayStatusToCSSClass} from "./shared";
import {ENGINEER_CATEGORY} from "../shared/constants";
import {getCalendarData} from "../../api";
import {dmyFmt, isoFmt} from "../shared/utils";
import {setCalendarPageFilter, setWagesParamsSelectionModeEnabled} from "../../store/actions";
import WagesTestParamsModal from "../shared/wages_params/WagesTestParamsModal";
import SelectionModeNotificationBlock from "../shared/wages_params/SelectionModeNotificationBlock";
import PropTypes from "prop-types";


class CalendarPage extends React.Component {
    static propTypes = {
        setPageFilters: PropTypes.func.isRequired,
        isSelectionModeEnabled: PropTypes.bool.isRequired,
        setSelectionModeEnabled: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props);

        // const customCSSclasses = {
        //     holidays: ['2023-04-25', '2023-05-01', '2023-06-02', '2023-08-15', '2023-11-01'],
        //     spring: {
        //         start: '2023-03-21',
        //         end: '2023-06-20'
        //     },
        //     summer: {
        //         start: '2023-06-21',
        //         end: '2023-09-22'
        //     },
        //     autumn: {
        //         start: '2023-09-23',
        //         end: '2023-12-21'
        //     },
        //     weekend: 'Sat,Sun',
        //     winter: day => isBefore(day, new Date(2023, 2, 21)) || isAfter(day, new Date(2023, 11, 21))
        // };

        // alternatively, customClasses can be a function accepting a moment object. For example:
        // day => (day.isBefore(moment([day.year(),2,21])) || day.isAfter(moment([day.year(),11,21]))) ? 'winter': 'summer'

        this.today = startOfToday();

        const startDate = startOfMonth(addMonths(this.today,-1));
        const endDate = endOfMonth(this.today, -1);

        const selectedDay = this.today;
        const selectedRange = [this.today, addDays(this.today, 5)];

        this.state = {
            isLoading: true,

            startDate: startDate,
            // startDateISO: isoFmt(startDate),
            endDate: endDate,
            // endDateISO: isoFmt(endDate),
            timelineRange: [startDate, endDate],

            selectedDay: selectedDay,
            selectedDayISO: isoFmt(selectedDay),
            selectedRange: selectedRange,
            selectedRangeISO: [isoFmt(selectedRange[0]), isoFmt(selectedRange[1])],
            selectRange: false,

            customCSSclasses: {},

            timeline: {},
            overrides: [],
            payAmountsByMonth: {},

            showSelectionModeBlock: props.isSelectionModeEnabled  // i.e. show the block if the selection mode was enabled when opening the page
        };
    }

    setState(state, callback) {
        state = typeof state === "function" ? state(this.state) : {...state};

        let startDate = state.startDate ?? this.state.startDate;
        let endDate = state.endDate ?? this.state.endDate;
        let timelineRangeChanged = state.startDate !== this.state.startDate || state.endDate !== this.state.endDate;
        let timelineRange = timelineRangeChanged ? [startDate, endDate] : this.state.timelineRange;

        let timelineInterval = {start: timelineRange[0], end: timelineRange[1]};

        let selectedDay = state.selectedDay ?? this.state.selectedDay;
        selectedDay = clamp(selectedDay, timelineInterval);
        let selectedDayISO = isoFmt(selectedDay);

        let [srStartDate, srEndDate] = state.selectedRange ?? this.state.selectedRange;
        let selectedRange = [clamp(srStartDate, timelineInterval), clamp(srEndDate, timelineInterval)];
        let selectedRangeISO = [isoFmt(selectedRange[0]), isoFmt(selectedRange[1])];

        state = {
            ...state,
            startDate, endDate, timelineRange,
            selectedDay, selectedRange, selectedDayISO, selectedRangeISO,
        };

        super.setState(state, callback);
    }

    componentDidMount = () => {
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.selectedEngineerId !== this.props.selectedEngineerId ||
            prevProps.selectedCalendarType !== this.props.selectedCalendarType ||
            prevProps.isSelectionModeEnabled !== this.props.isSelectionModeEnabled
        ) {
            this.fetchData();
        }
    }

    fetchData = async () => {
        try {
            if (!this.props.selectedEngineer)
                return;

            this.setState({isLoading: true});

            const startDate = isoFmt(this.state.startDate);
            const endDate = isoFmt(this.state.endDate);
            const selectedEngineer = this.props.selectedEngineer;
            const isSelectionModeEnabled = this.props.isSelectionModeEnabled;
            const data = await getCalendarData(startDate, endDate, selectedEngineer._id, isSelectionModeEnabled);
            let {timeline, overrides, payAmountsByMonth} = data;

            const customCSSclasses = {
                'vacationPaid': [],
                'vacationPaidPending': [],
                'vacationUnpaid': [],
                'vacationUnpaidPending': [],
                'timeOffPaid': [],
                'timeOffPaidPending': [],
                'timeOffUnpaid': [],
                'timeOffUnpaidPending': [],
                'workFromHome': [],
                'workFromHomeNWD': [],
                'workFromOffice': [],
                'workFromOfficeNWD': [],
                'fieldWorkInHomeCity': [],
                'fieldWorkInHomeCityNWD': [],
                'fieldWorkInOtherCity': [],
                'fieldWorkInOtherCityNWD': [],
                'onTheRoad': [],
                'onTheRoadNWD': [],
                'illness': [],
                'illnessPending': [],
                'nonWorkDay': [],
                'badge': [],
                'today': day => isSameDay(day, this.today),
            };

            for (const day in timeline) {
                const dayProps = timeline[day];
                const status = dayProps.status;
                const cssClass = transformDayStatusToCSSClass(status);

                if (cssClass)
                    customCSSclasses[cssClass].push(day);
                if (dayProps.statusOverride)
                    customCSSclasses.badge.push(day);
                if (!dayProps.isWorkday)
                    customCSSclasses.nonWorkDay.push(day);

                dayProps.date = parseISO(day);
            }

            overrides = overrides.map(item => {
                const affectedDatesISO = item.affectedDates;
                const affectedDates = item.affectedDates.map(date => parseISO(date));
                const affectedDatesRU = affectedDates.map(date => dmyFmt(date));
                const createdAt = parseISO(item.createdAt);
                const iconClassName = transformDayStatusToCSSClass(item.statusOverride);
                return { ...item, affectedDates, affectedDatesISO, affectedDatesRU, createdAt, iconClassName };
            })

            this.setState({customCSSclasses, timeline, overrides, payAmountsByMonth, isLoading: false});
        } catch (error) {
            console.error(error);
        }
    }

    changeCalendarType = (value) => {
        if (value === "YEAR") {
            const startDate = startOfYear(this.state.startDate);
            const endDate = endOfYear(this.state.startDate);

            this.props.setPageFilters({calendarType: 'YEAR'});
            this.setState({
                selectRange: false,
                // selectedRange: [this.today, addDays(this.today, 5)],
                startDate, endDate,
            }, this.fetchData);
        } else if (value === "MONTH") {
            const referenceDate = isSameYear(this.today, this.state.startDate) ? subMonths(this.today, 1) : this.state.startDate;

            const startDate = startOfMonth(referenceDate);
            const endDate = endOfMonth(addMonths(referenceDate, 1));

            this.props.setPageFilters({calendarType: 'MONTH'});
            this.setState({
                selectRange: false,
                // selectedRange: [this.today, addDays(this.today, 5)],
                startDate, endDate,
            }, this.fetchData);
        }
    };

    toggleSelectRange = () => this.setState(prevState => ({selectRange: !prevState.selectRange}));

    changeEngineer = (ev) => this.props.setPageFilters({engineerId: ev.target.value._id});

    // ***** YearCalendar ***** //

    onPrevYear = () => {
        const startDate = startOfYear(subYears(this.state.startDate, 1));
        const endDate = endOfYear(startDate);
        this.setState({startDate, endDate}, this.fetchData);
    }

    onNextYear = () => {
        const startDate = startOfYear(addYears(this.state.startDate, 1));
        const endDate = endOfYear(startDate);
        this.setState({startDate, endDate}, this.fetchData);
    }

    goToToday = () => {
        const today = this.today;
        const startDate = startOfYear(today), endDate = endOfYear(today);
        this.setState({ selectedDay: today, selectedRange: [today, addDays(today, 15)], startDate, endDate });
    }

    // ***** MonthCalendar ***** //

    onMonthChange = (date) => {
        const startDate = startOfMonth(date);
        const endDate = endOfMonth(addMonths(startDate, 1));
        this.setState({startDate, endDate}, this.fetchData);
    }

    // ***** //

    datePicked = (date) => this.setState({ selectedDay: date, selectedRange: [date, addDays(date, 5)]});

    rangePicked = (start, end) => this.setState({ selectedRange: [start, end], selectedDay: start });

    // ***** //

    paramsModalRef = new React.createRef();

    toggleSelectionMode = (ev) => this.props.setSelectionModeEnabled(ev.target.checked);

    openSelectionModeModal = () => this.paramsModalRef.current?.openModal();

    // ***** //

    showOverrideDeleteButton = (override) => {
        const currentUser = this.props.currentUser;
        return currentUser.isManagement ? true : override.authorId === currentUser._id
    }

    // ***** //

    render() {
        const {
            timeline, payAmountsByMonth,
            startDate, endDate, timelineRange,
            selectedDay, selectRange, selectedRange,
            selectedDayISO, selectedRangeISO,
            customCSSclasses,
            overrides,
            isLoading,
            showSelectionModeBlock,
        } = this.state;

        const { selectedEngineer, selectedCalendarType } = this.props;

        const { currentUser, selectEngineerItems, isSelectionModeEnabled } = this.props;
        const year = getYear(startDate);

        const selectedTimelineDay = selectedDay
            ? timeline[isoFmt(selectedDay)]
            : null;

        // there wasn't a "calendar-page-content", "calendar-page" had transform() and modal inside it had position: fixed, which didn't work
        // so now everything that needs to be transform()ed is in the separate container

        return (
            <div id="calendar-page">
                <WagesTestParamsModal ref={this.paramsModalRef} onParamsSubmit={this.fetchData} />

                <div id="calendar-page-content">
                    {isLoading && (
                        <div className="loading-overlay">
                            <Loading large/>
                        </div>
                    )}

                    {selectedCalendarType === "YEAR" &&
                        <div id="year-calendar">
                            <CalendarControls
                                year={year}
                                showTodayButton={false}
                                onPrevYear={this.onPrevYear}
                                onNextYear={this.onNextYear}
                                goToToday={this.goToToday}
                            />
                            <Calendar
                                year={year}
                                showDaysOfWeek={true}
                                forceFullWeeks={false}
                                showWeekSeparators={true}
                                firstDayOfWeek={1}
                                selectedDay={selectedDay}
                                selectRange={selectRange}
                                selectedRange={selectedRange}
                                onPickDate={(date, classes) => this.datePicked(date, classes)}
                                onPickRange={(start, end) => this.rangePicked(start, end)}
                                customClasses={customCSSclasses}
                            />
                        </div>
                    }

                    {selectedCalendarType === "MONTH" &&
                        <MonthCalendar
                            yearDays={timeline}
                            payAmountsByMonth={payAmountsByMonth}
                            month={startDate}
                            onMonthChange={this.onMonthChange}
                            customClasses={customCSSclasses}
                            forceFullWeeks

                            selectedDay={selectedDay}
                            selectRange={selectRange}
                            selectedRange={selectedRange}
                            onPickDate={this.datePicked}
                            onPickRange={this.rangePicked}
                        />
                    }

                    {showSelectionModeBlock && (
                        <SelectionModeNotificationBlock
                            wrapperClassName="my-2 py-2 bg-light-aqua"
                            isEnabled={isSelectionModeEnabled}
                            onSwitchClick={this.toggleSelectionMode}
                            onTextClick={this.openSelectionModeModal}
                        />
                    )}

                    <div className="columns mt-4">
                        <div className="column col-4">
                            <div className="h5 ml-2">Лог действий</div>
                                <AffectingOverridesLog overrides={overrides}
                                                       selectedRange={selectedRange}
                                                       selectedDay={selectedDay}
                                                       selectRange={selectRange}
                                                       onItemRemoved={this.componentDidMount}
                                                       showDeleteButton={this.showOverrideDeleteButton}
                                                       key={`1-${selectedRange[0]}-${selectedRange[1]}`}
                                />

                                <OverridesLog overrides={overrides}
                                              onItemRemoved={this.componentDidMount}
                                              showDeleteButton={this.showOverrideDeleteButton}
                                              key={`0-${selectedRange[0]}-${selectedRange[1]}`}
                                />
                        </div>

                        <div className="column col-3">
                            <div className="h5 ml-2">Действия</div>
                            {currentUser.isManagement &&
                                <ManagerActions selectedDay={selectedDay} selectedDayISO={selectedDayISO}
                                                selectedDayStatus={selectedTimelineDay?.status}
                                                selectRange={selectRange} selectedRange={selectedRange} selectedRangeISO={selectedRangeISO} toggleSelectRange={this.toggleSelectRange}
                                                timeline={timeline} onItemCreated={this.componentDidMount}
                                                selectedEngineer={selectedEngineer} selectEngineerItems={selectEngineerItems} onEngineerChange={this.changeEngineer}
                                                calendarType={selectedCalendarType} changeCalendarType={this.changeCalendarType}
                                                onRequestSubmitted={this.fetchData}
                                                currentUser={currentUser} onBonusAdded={this.fetchData}
                                />
                            }

                            {!currentUser.isManagement && currentUser.isEngineer &&
                                <EngineerActions selectedDay={selectedDay} selectedDayStatus={selectedTimelineDay?.status}
                                                 selectRange={selectRange} selectedRange={selectedRange} toggleSelectRange={this.toggleSelectRange}
                                                 timeline={timeline} onItemCreated={this.componentDidMount}
                                                 selectedEngineer={selectedEngineer}
                                                 calendarType={selectedCalendarType} changeCalendarType={this.changeCalendarType}
                                                 onRequestSubmitted={this.fetchData}
                                                 currentUser={currentUser}
                                />
                            }
                        </div>

                        <div className="column col-5">
                            <div className="h5 ml-2">В этот день</div>
                            {currentUser.isManagement && <ManagerDayContent timelineDay={selectedTimelineDay} />}
                            {!currentUser.isManagement && <EngineerDayContent timelineDay={selectedTimelineDay} />}

                            <div className="h5 ml-2 mt-4">Легенда</div>
                            <StatusLegend timelineRange={timelineRange} selectRange={selectRange} selectedRange={selectedRange} timeline={timeline}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const selectEngineerItems = state.engineersByCategory[ENGINEER_CATEGORY.ACTIVE]
        .map(({_id, nameLF}) => ({ _id: _id, label: nameLF }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const currentUser = state.currentUser;
    const {engineerId, calendarType} = state.calendarPage;
    const isSelectionModeEnabled = state.wagesParamsSelectionModeEnabled;

    let selectedEngineer = undefined;
    if (currentUser.isManagement) {
        selectedEngineer = selectEngineerItems.find(({_id}) => _id === engineerId);
        selectedEngineer = selectedEngineer || selectEngineerItems.find(({_id}) => _id === currentUser._id);
        selectedEngineer = selectedEngineer || selectEngineerItems[0];
    } else {
        selectedEngineer = selectEngineerItems.find(({_id}) => _id === currentUser._id);
    }

    let selectedEngineerId = selectedEngineer?._id;

    let selectedCalendarType = calendarType || "MONTH";

    return {
        selectEngineerItems, selectedEngineerId, selectedEngineer,
        selectedCalendarType,
        isSelectionModeEnabled,
        currentUser
    }
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    setPageFilters: (state) => dispatch(setCalendarPageFilter(state)),
    setSelectionModeEnabled: (flag) => dispatch(setWagesParamsSelectionModeEnabled(flag))
});

export default connect(mapStateToProps, mapDispatchToProps)(CalendarPage);
